import { Box, Icon, Typography } from "@bca/ui-library";
import React, { useEffect, useRef, useState } from "react";
import { FocusScope } from "react-aria";
import styled from "styled-components";
import {
  color,
  ColorProps,
  layout,
  LayoutProps,
  space,
  SpaceProps,
} from "styled-system";

import { formatMoney } from "./buyingOptions/formatMoney";

interface HPISpecCheckOption {
  shortDescription?: string;
  longDescription?: string;
  basicPrice?: number;
  optionPrice?: number;
  optionKind?: string;
}

interface HeadingButtonPRops extends LayoutProps, ColorProps, SpaceProps {
  onClick: () => void;
  children: React.ReactNode;
}

const HeadingButton = styled.button<HeadingButtonPRops>`
  ${layout}
  ${color}
  ${space}
  border: none;
  font-size: 16px;
  display: flex;
  outline: none;
`;

const AnimatedIconContainer = styled(Box)`
  transform: translate(0, -50%);

  .collapsed {
    transition: transform 0.3s;
    transform: rotate(0deg);
  }
  .expanded {
    transition: transform 0.3s;
    transform: rotate(-180deg);
  }
`;

const StyledTypography = styled(Typography)<LayoutProps>`
  ${layout}
`;

const GridBox = styled(Box)`
  display: grid;
  grid-auto-flow: column;

  overflow: hidden;
  transition: max-height ${400 / 1000}s, opacity ${(400 * 2) / 1000}s;
`;

const OptionalEquipmentAccordion: React.FunctionComponent<{
  hpiOptions: HPISpecCheckOption[];
  optionsPrice: number;
}> = ({ hpiOptions, optionsPrice }) => {
  const [isOpen, setOpen] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const [opacity, setOpacity] = useState(isOpen ? 1 : 0);
  const [overflow, setOverflow] = useState(isOpen ? "visible" : "hidden");
  const ref = useRef(null);

  const resizeOptionSection = () => {
    const boxHeight = ref.current.scrollHeight;
    setMaxHeight(boxHeight);
  };

  useEffect(() => {
    let timeout: number | NodeJS.Timeout = 0;
    let secondTimeout: number | NodeJS.Timeout = 0;
    const boxHeight =
      ref && ref.current !== null ? ref.current.scrollHeight : 0;

    if (isOpen) {
      timeout = setTimeout(() => {
        setMaxHeight(boxHeight);
        setOpacity(1);
        secondTimeout = setTimeout(() => {
          setOverflow("visible");
        }, 400);
      }, 0);
      return;
    }
    setOpacity(0);
    timeout = setTimeout(() => {
      setMaxHeight(0);
      setOverflow("hidden");
      secondTimeout = setTimeout(() => {}, 400);
    }, 0);

    return () => {
      clearTimeout(timeout);
      clearTimeout(secondTimeout);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener("resize", resizeOptionSection);
      return () => {
        window.removeEventListener("resize", resizeOptionSection);
      };
    }
  }, [isOpen]);

  return (
    <Box
      backgroundColor="#fafafa"
      css={`
        box-shadow: -100px 0 5px -2px #fafafa, 100px 0 5px -2px #fafafa;
      `}
    >
      <HeadingButton
        width="100%"
        height="100%"
        bg="transparent"
        padding="0 0 0 0"
        aria-expanded={isOpen}
        onClick={() => {
          setOpen(!isOpen);
        }}
        data-en="click"
        data-ec="accordion"
        data-el="optional-equipment-new"
        data-ea={isOpen ? "collapse" : "expand"}
      >
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box display="flex">
            <StyledTypography
              variant="h6"
              marginTop="18px"
              marginBottom="10px"
              width={{ mobile: "150px", tablet: "224px", desktop: "224px" }}
            >
              Optional Equipment when new
            </StyledTypography>
            <AnimatedIconContainer
              width={25}
              height={25}
              marginTop={{ mobile: "37px", tablet: "29px", desktop: "29px" }}
            >
              <Icon
                className={isOpen ? "expanded" : "collapsed"}
                aria-hidden={true}
                name="angle-down"
                weight="regular"
                color="primary-light-blue"
                width={25}
                height={25}
              />
            </AnimatedIconContainer>
          </Box>
          {Boolean(optionsPrice) ? (
            <Box
              display="flex"
              alignContent="center"
              marginBottom="10px"
              flexDirection={{
                mobile: "column",
                tablet: "row",
                desktop: "row",
              }}
              alignItems="top"
            >
              <Typography
                variant="h6"
                marginTop="18px"
                marginBottom="0px"
                textAlign="right"
              >
                {formatMoney(optionsPrice)}
              </Typography>
              <Typography
                variant="body"
                color="grey-blue"
                marginTop={{
                  mobile: "0px",
                  tablet: "16px",
                  desktop: "16.5px",
                }}
                marginLeft="3px"
                marginBottom="9px"
                textAlign="center"
              >
                inc. VAT
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="h6"
              margin="0"
              marginRight="0px"
              marginTop={{ mobile: "23px", tablet: "17px", desktop: "17px" }}
            >
              -
            </Typography>
          )}
        </Box>
      </HeadingButton>
      {
        <FocusScope contain restoreFocus autoFocus>
          <GridBox
            ref={ref}
            hidden={!isOpen}
            css={`
              max-height: ${maxHeight}px;
              opacity: ${opacity};
              overflow: ${overflow};

              @media screen and (max-width: 544px) {
                grid-auto-flow: row;
                grid-template-columns: 1fr;
                gap: 0px;
              }
              @media screen and (min-width: 544px) {
                grid-auto-flow: column;
                grid-template-rows: repeat(
                  ${Math.ceil(hpiOptions.length / 2)},
                  1fr
                );
                grid-template-columns: 47% 47%;
                column-gap: 6%;
              }

              @media print {
                display: inline;
                overflow: visible;
                opacity: 1;
              }
            `}
          >
            {Boolean(hpiOptions) &&
              hpiOptions.map((option) => (
                <Box
                  key={option.optionPrice}
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <StyledTypography
                    variant="body"
                    color="grey-blue"
                    marginTop="0px"
                    width="80%"
                  >
                    {option.shortDescription}
                  </StyledTypography>
                  {Boolean(option.optionPrice) ? (
                    <StyledTypography
                      variant="h6"
                      margin={0}
                      mt="1px"
                      width="7em"
                      textAlign="right"
                    >
                      {formatMoney(option.optionPrice)}
                    </StyledTypography>
                  ) : (
                    <Typography variant="h6" margin="0">
                      -
                    </Typography>
                  )}
                </Box>
              ))}
          </GridBox>
        </FocusScope>
      }
    </Box>
  );
};

export default OptionalEquipmentAccordion;
